// src/components/gavion/meetings/MeetingNoteCard.tsx
import { useState } from "react";
import { useLocaleState, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { validateShareUrl } from "@/components/gavion/domain/granola";
import { formatLeadDateTime } from "@/components/gavion/domain/torontoTime";
import { MarkdownBlocks } from "./MarkdownBlocks";
import { SuggestedTasks } from "./SuggestedTasks";
import type { MeetingNoteRow } from "./types";

const COLLAPSED_BLOCKS = 6;

export const MeetingNoteCard = ({
  note,
  collapsed = true,
  variant = "meeting",
}: {
  note: MeetingNoteRow;
  collapsed?: boolean;
  /** GAVION granola-calls: "call" = under a call entry (🎙️ Granola line). */
  variant?: "meeting" | "call";
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const [expanded, setExpanded] = useState(!collapsed);
  const summary = note.summary_markdown ?? "";
  const share = validateShareUrl(note.share_url);
  const people = note.attendees
    .map((a) => a.name ?? a.email)
    .filter(Boolean)
    .join(", ");
  return (
    <article className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>
          {translate(
            variant === "call"
              ? "gavion.meetings.card.granola_call"
              : "gavion.meetings.card.granola",
          )}
        </span>
        {note.started_at ? (
          <time dateTime={note.started_at}>
            {formatLeadDateTime(note.started_at, locale === "en" ? "en" : "fr")}
          </time>
        ) : null}
        {share ? (
          <a
            className="text-primary underline"
            href={share}
            target="_blank"
            rel="noopener noreferrer"
          >
            {translate("gavion.meetings.card.open_granola")}
          </a>
        ) : null}
      </div>
      {people ? (
        <p className="text-xs text-muted-foreground">
          {translate("gavion.meetings.card.attendees")} : {people}
        </p>
      ) : null}
      {/* GAVION granola-calls: the rep forgot to stop the note (spec §2) */}
      {note.spans_calls ? (
        <p className="text-sm font-medium">
          {translate("gavion.meetings.card.spans_calls")}
        </p>
      ) : null}
      <MarkdownBlocks
        markdown={summary}
        maxBlocks={expanded ? undefined : COLLAPSED_BLOCKS}
      />
      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => setExpanded((v) => !v)}
      >
        {translate(
          expanded
            ? "gavion.meetings.card.show_less"
            : "gavion.meetings.card.show_more",
        )}
      </Button>
      {note.match_status === "matched" ? (
        <SuggestedTasks noteId={note.id} summaryMarkdown={summary} />
      ) : null}
    </article>
  );
};
